import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import AssistantIcon from '@mui/icons-material/Assistant'
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import DifferenceOutlinedIcon from '@mui/icons-material/DifferenceOutlined'
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import { useKeycloak } from '@react-keycloak/web'
import { Box } from '@mui/material'
import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useLeftPanelMode } from '@/components/LeftPanelMode'
import { IS_AI_AGENTS_PREVIEW_ENABLED, IS_PREVIEW_PAGES_ENABLED } from '@/config'
import { ROUTES } from '@/constants'
import { toggleAIPanel } from '@/features'
import { useAppContext, useAppDispatch, useAppStore } from '@/hooks'
import { PackageIcon } from '@/pages/User/pages/DeploymentPackages'
import { hasAiAccess } from '@/utils/roleAccess'

import { ActivityBarButton } from './ActivityBarButton'

export const ActivityBar = () => {
	const { t } = useAppContext()
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const location = useLocation()
	const { keycloak } = useKeycloak()
	const { mode, openExplorer, openSearch } = useLeftPanelMode()
	const aiPanelOpen = useAppStore((state) => state.aiSlice.aiPanelOpen)
	const hasAiFeatureAccess = hasAiAccess(keycloak.tokenParsed)

	const isProjectView = location.pathname === ROUTES.PROJECT_VIEW
	const isSettings = location.pathname.startsWith(ROUTES.SETTINGS)

	const handleOpenExplorer = useCallback(() => {
		openExplorer()
		navigate(ROUTES.PROJECT_VIEW)
	}, [navigate, openExplorer])

	const handleOpenSearch = useCallback(() => {
		openSearch()
		navigate(ROUTES.PROJECT_VIEW)
	}, [navigate, openSearch])

	const handleOpenSettings = useCallback(() => {
		navigate(ROUTES.SETTINGS)
	}, [navigate])

	const handleToggleAiChat = useCallback(() => {
		dispatch(toggleAIPanel())
	}, [dispatch])

	const handlePreviewNavigate = useCallback(
		(path: string) => {
			navigate(path)
		},
		[navigate],
	)

	return (
		<Box
			component="nav"
			aria-label={t('ACTIVITY_BAR')}
			data-testid="activity-bar"
			sx={{
				display: 'flex',
				width: 56,
				flexShrink: 0,
				flexDirection: 'column',
				alignItems: 'stretch',
				pt: '6px',
				pb: '6px',
				bgcolor: '#F4F4F4',
				borderRight: '1px solid #E4E4E4',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'stretch',
					width: '100%',
				}}
			>
				<ActivityBarButton
					icon={DescriptionOutlinedIcon}
					label={t('ACTIVITY_BAR_EXPLORER')}
					active={isProjectView && mode === 'explorer'}
					onClick={handleOpenExplorer}
					testId="activity-bar-explorer"
				/>
				<ActivityBarButton
					icon={SearchOutlinedIcon}
					label={t('SEARCH')}
					active={isProjectView && mode === 'search'}
					onClick={handleOpenSearch}
					testId="activity-bar-search"
				/>
				{IS_PREVIEW_PAGES_ENABLED && (
					<>
						<ActivityBarButton
							icon={HubOutlinedIcon}
							label={t('NAVBAR_TAB_ONTOLOGY')}
							active={location.pathname.startsWith(ROUTES.ONTOLOGY)}
							onClick={() => handlePreviewNavigate(ROUTES.ONTOLOGY)}
							testId="activity-bar-ontology"
						/>
						<ActivityBarButton
							icon={BarChartOutlinedIcon}
							label={t('ACTIVITY_BAR_REPORTS')}
							active={location.pathname.startsWith(ROUTES.REPORTS)}
							onClick={() => handlePreviewNavigate(ROUTES.REPORTS)}
							testId="activity-bar-reports"
						/>
						<ActivityBarButton
							icon={AccountTreeOutlinedIcon}
							label={t('ACTIVITY_BAR_BRANCHES')}
							active={location.pathname.startsWith(ROUTES.BRANCHES)}
							onClick={() => handlePreviewNavigate(ROUTES.BRANCHES)}
							testId="activity-bar-branches"
						/>
						<ActivityBarButton
							icon={DifferenceOutlinedIcon}
							label={t('ACTIVITY_BAR_MERGE_REQUESTS')}
							active={location.pathname.startsWith(ROUTES.MERGE_REQUESTS)}
							onClick={() => handlePreviewNavigate(ROUTES.MERGE_REQUESTS)}
							testId="activity-bar-merge-requests"
						/>
						<ActivityBarButton
							icon={LayersOutlinedIcon}
							label={t('ACTIVITY_BAR_RELEASES')}
							active={location.pathname.startsWith(ROUTES.RELEASES)}
							onClick={() => handlePreviewNavigate(ROUTES.RELEASES)}
							testId="activity-bar-releases"
						/>
					</>
				)}
				{IS_PREVIEW_PAGES_ENABLED && IS_AI_AGENTS_PREVIEW_ENABLED && (
					<ActivityBarButton
						icon={SmartToyOutlinedIcon}
						label={t('ACTIVITY_BAR_AI_AGENTS')}
						active={location.pathname.startsWith(ROUTES.AI_AGENTS)}
						onClick={() => handlePreviewNavigate(ROUTES.AI_AGENTS)}
						testId="activity-bar-ai-agents"
					/>
				)}
				{hasAiFeatureAccess && (
					<ActivityBarButton
						icon={AssistantIcon}
						label={t('AI_ASSISTANT')}
						active={aiPanelOpen}
						onClick={handleToggleAiChat}
						testId="activity-bar-ai-chat"
					/>
				)}
			</Box>

			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'stretch',
					width: '100%',
					mt: 'auto',
				}}
			>
				{IS_PREVIEW_PAGES_ENABLED && (
					<>
						<ActivityBarButton
							icon={GridViewOutlinedIcon}
							label={t('ACTIVITY_BAR_DEPLOYMENT_DASHBOARD')}
							active={location.pathname.startsWith(
								ROUTES.DEPLOYMENT_DASHBOARD,
							)}
							onClick={() =>
								handlePreviewNavigate(ROUTES.DEPLOYMENT_DASHBOARD)
							}
							testId="activity-bar-deployment-dashboard"
						/>
						<ActivityBarButton
							icon={PackageIcon}
							label={t('ACTIVITY_BAR_DEPLOYMENT_PACKAGES')}
							active={location.pathname.startsWith(
								ROUTES.DEPLOYMENT_PACKAGES,
							)}
							onClick={() =>
								handlePreviewNavigate(ROUTES.DEPLOYMENT_PACKAGES)
							}
							testId="activity-bar-deployment-packages"
						/>
						<ActivityBarButton
							icon={TuneOutlinedIcon}
							label={t('ACTIVITY_BAR_SYSTEM_SETTINGS')}
							active={location.pathname.startsWith(ROUTES.SYSTEM_SETTINGS)}
							onClick={() => handlePreviewNavigate(ROUTES.SYSTEM_SETTINGS)}
							testId="activity-bar-system-settings"
						/>
					</>
				)}
				<ActivityBarButton
					icon={SettingsOutlinedIcon}
					label={t('NAVBAR_MENU_SETTINGS')}
					active={isSettings}
					onClick={handleOpenSettings}
					testId="activity-bar-settings"
				/>
			</Box>
		</Box>
	)
}
